import { Temporal } from "temporal-polyfill";

export const WEEKDAYS = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
];

const DEFAULT_FROM = 800;
const DEFAULT_TILL = 2300;

export const hhmmToTime = (value) => {
    const padded = String(value).padStart(4, "0");
    return `${padded.slice(0, 2)}:${padded.slice(-2)}`;
};

export const timeToHhmm = (value) => {
    const match = /^(\d{2}):(\d{2})$/.exec(value || "");
    if (!match) {
        return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) {
        return null;
    }
    return hours * 100 + minutes;
};

export const runsPastMidnight = (from, till) => from != null && till != null && till < from;

const zonedInstant = (date, time, timeZone) => {
    const [year, month, day] = date.split("-").map(Number);
    const [hour, minute] = time.split(":").map(Number);
    return Temporal.ZonedDateTime.from({
        timeZone,
        year,
        month,
        day,
        hour,
        minute,
        second: 0,
    }).toString({ timeZoneName: "never" });
};

const nextDate = (date) => Temporal.PlainDate.from(date).add({ days: 1 }).toString();

export const formatInLocation = (iso, timeZone) => {
    const zoned = Temporal.Instant.from(iso).toZonedDateTimeISO(timeZone);
    const pad = (value) => String(value).padStart(2, "0");
    return `${pad(zoned.day)}-${pad(zoned.month)}-${zoned.year}, ${pad(zoned.hour)}:${pad(zoned.minute)}`;
};

export const formatCalendarDate = (isoDate) => {
    const [year, month, day] = isoDate.split("-");
    return `${day}-${month}-${year}`;
};

export const weekDraftFromRows = (rows) => {
    const byDay = Object.fromEntries(WEEKDAYS.map((day) => [day, []]));
    for (const row of rows) {
        if (byDay[row.day]) {
            byDay[row.day].push(row);
        }
    }
    const days = [];
    const extras = [];
    for (const day of WEEKDAYS) {
        const [first, ...rest] = byDay[day];
        if (!first) {
            days.push({
                day,
                from: hhmmToTime(DEFAULT_FROM),
                till: hhmmToTime(DEFAULT_TILL),
                open: true,
                created: true,
            });
        } else {
            days.push({
                id: first.id,
                day,
                from: hhmmToTime(first.from),
                till: hhmmToTime(first.till),
                open: Boolean(first.open),
                created: false,
            });
            extras.push(...rest);
        }
    }
    return { days, extras };
};

export const buildWeekPayload = (days, deleteIds) => ({
    days: days.map((day) => {
        const from = timeToHhmm(day.from);
        const till = timeToHhmm(day.till);
        const entry = {
            day: day.day,
            from,
            till,
            open: day.open,
        };
        if (day.id) {
            entry.id = day.id;
        }
        return entry;
    }),
    deleteIds,
});

export const closureInterval = ({ mode, startDate, endDate, from, till, timeZone }) => {
    if (mode === "days") {
        return {
            start: zonedInstant(startDate, "00:00", timeZone),
            end: zonedInstant(nextDate(endDate), "00:00", timeZone),
        };
    }
    const fromHhmm = timeToHhmm(from);
    const tillHhmm = timeToHhmm(till);
    if (fromHhmm == null || tillHhmm == null || fromHhmm === tillHhmm) {
        return null;
    }
    const endOn = runsPastMidnight(fromHhmm, tillHhmm) ? nextDate(startDate) : startDate;
    return {
        start: zonedInstant(startDate, from, timeZone),
        end: zonedInstant(endOn, till, timeZone),
    };
};

const calendarDay = (value) => String(value).slice(0, 10);

const previousDate = (date) => Temporal.PlainDate.from(date).subtract({ days: 1 }).toString();

const instantAt = (date, time, timeZone) => Temporal.Instant.from(zonedInstant(date, time, timeZone));

// Slot generation keeps the oldest open row and lets one override replace that weekday.
export const effectiveHoursForDate = (rows, overrides, date) => {
    const covering = overrides.filter((row) => (
        calendarDay(row.start_date) <= date && calendarDay(row.end_date) >= date
    ));
    if (covering.length > 1) {
        return { status: "ambiguous" };
    }
    if (covering.length === 1) {
        return { status: "open", from: Number(covering[0].from), till: Number(covering[0].till) };
    }
    const weekday = WEEKDAYS[Temporal.PlainDate.from(date).dayOfWeek - 1];
    const row = rows
        .filter((item) => item.open && item.day === weekday)
        .sort((left, right) => left.id - right.id)[0];
    if (!row) {
        return { status: "closed" };
    }
    return { status: "open", from: Number(row.from), till: Number(row.till) };
};

const openWindow = (date, hours, timeZone) => {
    if (hours.status !== "open") {
        return null;
    }
    const endDate = runsPastMidnight(hours.from, hours.till) ? nextDate(date) : date;
    return {
        start: instantAt(date, hhmmToTime(hours.from), timeZone),
        end: instantAt(endDate, hhmmToTime(hours.till), timeZone),
    };
};

const coversWindow = (closure, window) => {
    const start = Temporal.Instant.from(closure.start);
    const end = Temporal.Instant.from(closure.end);
    return start.epochMilliseconds <= window.start.epochMilliseconds
        && end.epochMilliseconds >= window.end.epochMilliseconds;
};

const overlaps = (closure, start, end) => {
    const closureStart = Temporal.Instant.from(closure.start);
    const closureEnd = Temporal.Instant.from(closure.end);
    return closureStart.epochMilliseconds < end.epochMilliseconds
        && closureEnd.epochMilliseconds > start.epochMilliseconds;
};

export const calendarDateOf = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    const pad = (part) => String(part).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const dayIsOutsideOpeningHours = (schedule, date, timeZone) => {
    const hours = effectiveHoursForDate(schedule.rows, schedule.overrides, date);
    const window = openWindow(date, hours, timeZone);
    if (!window) {
        return true;
    }
    return schedule.closures.some((closure) => coversWindow(closure, window));
};

export const timeIsOutsideOpeningHours = (schedule, { date, time, duration, timeZone }) => {
    if (!date || !time || !timeZone) {
        return false;
    }
    const start = instantAt(date, time, timeZone);
    const end = start.add({ minutes: duration || 0 });
    const windows = [date, previousDate(date)]
        .map((day) => openWindow(day, effectiveHoursForDate(schedule.rows, schedule.overrides, day), timeZone))
        .filter(Boolean);
    const inside = windows.some((window) => (
        start.epochMilliseconds >= window.start.epochMilliseconds
        && end.epochMilliseconds <= window.end.epochMilliseconds
    ));
    if (!inside) {
        return true;
    }
    return schedule.closures.some((closure) => overlaps(closure, start, end));
};

export const overridePayload = ({ startDate, endDate, from, till }) => {
    const fromHhmm = timeToHhmm(from);
    const tillHhmm = timeToHhmm(till);
    if (fromHhmm == null || tillHhmm == null || fromHhmm === tillHhmm) {
        return null;
    }
    return {
        start_date: startDate,
        end_date: endDate,
        from: fromHhmm,
        till: tillHhmm,
    };
};
